import { useState, useEffect, useRef, useCallback } from "react";
import { API_BASE_URL } from "../config";
import LoadingScreen from "../components/common/LoadingScreen";
import { navigateToDashboard, getEffectiveLangId } from "../utils/navigationHelpers";
import { authFetch } from "../utils/auth";
import { speakText, stopSpeech, hasVoiceForLang } from "../utils/audioHelpers";
import SpeakerButton from "../components/common/SpeakerButton";
import "./../assets/css/vocabtest.css";
import "./../assets/css/flashcardgame.css";
import "./../assets/css/listening.css";
import InGameSettingsModal, { SettingsIcon } from "../components/common/InGameSettingsModal";
import BackToDashboardButton from "../components/common/BackToDashboardButton";
import SessionCompleteCard from "../components/common/SessionCompleteCard";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
function getLangTag(langId) { return LANG_TAG[parseInt(langId, 10)] ?? "de-DE"; }

// How many recent phrases to ask the generator not to repeat.
const AVOID_HISTORY = 5;

// The "Play slower" button drops to this fraction of the chosen rate.
const SLOW_RATE_FACTOR = 0.6;

const DEFAULT_SPEECH_RATE = 1.0;


function ListeningGame() {
    const urlParams = new URLSearchParams(window.location.search);
    const unit = urlParams.get("unit");
    const langId = getEffectiveLangId(urlParams.get("lang_id"));
    const langTag = getLangTag(langId);

    // generating | listening | evaluating | feedback | error | summary
    const [phase, setPhase] = useState("generating");
    const [round, setRound] = useState(null);
    const [answer, setAnswer] = useState("");
    const [result, setResult] = useState(null);
    const [errorInfo, setErrorInfo] = useState(null);
    const [stats, setStats] = useState({ rounds: 0, judged: 0, understood: 0, distinct: 0 });
    const [noVoice, setNoVoice] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settings, setSettings] = useState(null);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsMessage, setSettingsMessage] = useState("");

    const speechRate = settings?.sual_speech_rate || DEFAULT_SPEECH_RATE;
    // Read through a ref so the speak-on-arrival effect never fires on a rate change.
    const speechRateRef = useRef(DEFAULT_SPEECH_RATE);
    speechRateRef.current = speechRate;

    // Session bookkeeping. Refs rather than state: these are read inside async
    // callbacks and must never be stale.
    const roundNoRef = useRef(0);
    const lastSeenRef = useRef(new Map());
    const distinctRef = useRef(new Set());
    const recentPhrasesRef = useRef([]);
    const spokenKeyRef = useRef(null);

    const goToDashboard = useCallback(() => {
        stopSpeech();
        navigateToDashboard("listening");
    }, []);

    const generateRound = useCallback(() => {
        setPhase("generating");
        setAnswer("");
        setResult(null);
        setErrorInfo(null);

        authFetch(`${API_BASE_URL}/api/sual/generate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                lang_id: parseInt(langId, 10),
                unit: parseInt(unit, 10),
                avoid_sentences: recentPhrasesRef.current
            })
        })
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (!ok || !data.ok) {
                    setErrorInfo({ code: data.code || "openai_error", message: data.error || "Something went wrong." });
                    setPhase("error");
                    return;
                }

                roundNoRef.current += 1;
                recentPhrasesRef.current = [...recentPhrasesRef.current, data.sentence].slice(-AVOID_HISTORY);
                setRound(data);
                setPhase("listening");
            })
            .catch(err => {
                console.error(err);
                setErrorInfo({ code: "network", message: "Could not reach the server. Please try again." });
                setPhase("error");
            });
    }, [langId, unit]);

    // Bootstrap. A missing parameter is a broken link rather than a game state.
    useEffect(() => {
        if (!langId || unit === null) {
            setErrorInfo({ code: "bad_link", message: "This listening link is missing information. Please start from the dashboard." });
            setPhase("error");
            return;
        }
        if (!hasVoiceForLang(getLangTag(langId))) {
            setNoVoice(true);
        }

        // Phrase length is applied server-side; the rate is needed here.
        authFetch(`${API_BASE_URL}/api/user/settings`)
            .then(res => res.json())
            .then(data => { if (data.settings) setSettings(data.settings); })
            .catch(err => console.error("Error fetching listening settings:", err));

        generateRound();
        return () => stopSpeech();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Speak each new phrase exactly once on arrival. The phrase is never shown as
    // text in this phase, so unlike the other games this always plays regardless
    // of any auto-play preference.
    useEffect(() => {
        if (phase !== "listening" || !round) return;

        const key = `${roundNoRef.current}`;
        if (spokenKeyRef.current === key) return;
        spokenKeyRef.current = key;

        const timeoutId = setTimeout(
            () => speakText(round.sentence, langTag, { rate: speechRateRef.current }), 300);
        return () => clearTimeout(timeoutId);
    }, [phase, round, langTag]);

    const submitAnswer = () => {
        if (!round || phase !== "listening") return;
        stopSpeech();
        setPhase("evaluating");

        const currentRound = roundNoRef.current;
        const tokens = round.tokens.map(t => {
            const lastSeen = lastSeenRef.current.get(t.word_id);
            return {
                word_id: t.word_id,
                surface: t.surface,
                // Counted in rounds starting at 1; omitted when the word has not
                // appeared yet this session so the server uses its default gap.
                ...(lastSeen ? { rounds_since_seen: currentRound - lastSeen } : {})
            };
        });

        authFetch(`${API_BASE_URL}/api/sual/evaluate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                lang_id: parseInt(langId, 10),
                unit: parseInt(unit, 10),
                sentence: round.sentence,
                translation: round.translation,
                user_answer: answer,
                tokens
            })
        })
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (!ok || !data.ok) {
                    setErrorInfo({ code: data.code || "openai_error", message: data.error || "Something went wrong." });
                    setPhase("error");
                    return;
                }

                data.words.forEach(w => {
                    if (w.word_id === null || w.word_id === undefined) return;
                    lastSeenRef.current.set(w.word_id, currentRound);
                    distinctRef.current.add(w.word_id);
                });

                setStats(prev => ({
                    rounds: prev.rounds + 1,
                    judged: prev.judged + (data.evaluated_count || 0),
                    understood: prev.understood + (data.understood_count || 0),
                    distinct: distinctRef.current.size
                }));

                setResult(data);
                setPhase("feedback");
            })
            .catch(err => {
                console.error(err);
                setErrorInfo({ code: "network", message: "Could not reach the server. Please try again." });
                setPhase("error");
            });
    };

    // Unlike the other games' in-game menus, these settings persist: they are the
    // same values shown in Account Settings, saved through the same endpoint.
    const saveSettings = (next) => {
        setSettings(next);
        setSettingsSaving(true);
        setSettingsMessage("");

        authFetch(`${API_BASE_URL}/api/user/settings`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ settings: next })
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    // The server clamps, so its values win.
                    setSettings(data.settings);
                    setSettingsMessage("Saved. New lengths apply to the next phrase.");
                } else {
                    setSettingsMessage(data.error || "Could not save settings.");
                }
            })
            .catch(err => {
                console.error(err);
                setSettingsMessage("Could not save settings.");
            })
            .finally(() => setSettingsSaving(false));
    };

    const settingsMenu = (
        <InGameSettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            title="Settings"
        >
            {settings ? (
                <>
                    <div className="in-game-settings-section">
                        <label className="in-game-settings-checkbox-label" htmlFor="sual-rate">
                            Speaking speed: {speechRate.toFixed(2)}x
                        </label>
                        <input
                            id="sual-rate"
                            type="range"
                            min="0.5"
                            max="1.6"
                            step="0.05"
                            value={speechRate}
                            className="in-game-settings-range"
                            onChange={e => setSettings({
                                ...settings,
                                sual_speech_rate: parseFloat(e.target.value)
                            })}
                            onMouseUp={e => saveSettings({
                                ...settings,
                                sual_speech_rate: parseFloat(e.target.value)
                            })}
                            onTouchEnd={e => saveSettings({
                                ...settings,
                                sual_speech_rate: parseFloat(e.target.value)
                            })}
                        />
                        <div className="in-game-settings-desc" style={{ marginLeft: 0 }}>
                            How fast phrases are read out. Drag, then press Test to hear it.
                        </div>
                        <button
                            type="button"
                            className="in-game-settings-action-btn"
                            style={{ alignSelf: "flex-start", marginTop: "4px" }}
                            onClick={() => speakText(
                                round?.sentence || "Hallo", langTag, { rate: speechRate })}
                        >
                            Test speed
                        </button>
                    </div>

                    <div className="in-game-settings-section">
                        <label className="in-game-settings-checkbox-label">Phrase length (words)</label>
                        <div className="in-game-settings-length-row">
                            <input
                                type="number"
                                min="2"
                                max="20"
                                className="in-game-settings-number-input"
                                value={settings.sual_min_words ?? ""}
                                onChange={e => setSettings({
                                    ...settings,
                                    sual_min_words: parseInt(e.target.value, 10)
                                })}
                            />
                            <span>to</span>
                            <input
                                type="number"
                                min="2"
                                max="30"
                                className="in-game-settings-number-input"
                                value={settings.sual_max_words ?? ""}
                                onChange={e => setSettings({
                                    ...settings,
                                    sual_max_words: parseInt(e.target.value, 10)
                                })}
                            />
                            <button
                                type="button"
                                className="in-game-settings-action-btn"
                                onClick={() => saveSettings(settings)}
                                disabled={settingsSaving}
                            >
                                {settingsSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                        <div className="in-game-settings-desc" style={{ marginLeft: 0 }}>
                            Applies from the next phrase onwards.
                        </div>
                    </div>

                    {settingsMessage && (
                        <div className="in-game-settings-feedback">
                            {settingsMessage}
                        </div>
                    )}
                </>
            ) : (
                <div className="in-game-settings-section">
                    <LoadingScreen inline title="Loading settings..." size={24} />
                </div>
            )}
        </InGameSettingsModal>
    );

    const settingsButton = (
        <button
            type="button"
            aria-label="Settings"
            className="in-game-settings-btn"
            onClick={() => setShowSettings(true)}
        >
            <SettingsIcon size={28} />
        </button>
    );

    const handleKeyDown = (e) => {
        // Ctrl/Cmd+Enter submits, so Enter stays available for multi-line answers.
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            submitAnswer();
        }
    };

    const understoodPercent = stats.judged > 0
        ? Math.round((stats.understood / stats.judged) * 100)
        : 0;

    const dashboardButton = (
        <BackToDashboardButton floating onClick={goToDashboard} />
    );

    const statsBar = (
        <div className="sual-stats-bar">
            <span>Round {Math.max(roundNoRef.current, 1)}</span>
            {stats.judged > 0 && (
                <>
                    <span>{stats.understood}/{stats.judged} words understood ({understoodPercent}%)</span>
                    <span>{stats.distinct} distinct words</span>
                </>
            )}
        </div>
    );

    if (phase === "error") {
        const isKeyProblem = errorInfo?.code === "no_api_key" || errorInfo?.code === "bad_api_key";
        const isBadLink = errorInfo?.code === "bad_link";
        return (
            <div className="sual-root">
                {dashboardButton}
                {settingsButton}
                {settingsMenu}
                <div className="sual-error-card">
                    <h2>Can't start this round</h2>
                    <p>{errorInfo?.message}</p>
                    {isKeyProblem && (
                        <p className="sual-error-hint">
                            Open Account Settings from the dashboard to add or update your key.
                        </p>
                    )}
                    {!isKeyProblem && !isBadLink && (
                        <button type="button" className="test-submit-btn" onClick={generateRound}>
                            Try again
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (phase === "summary") {
        return (
            <SessionCompleteCard
                title="Listening Session Complete"
                stats={[
                    { label: "Phrases heard", value: stats.rounds },
                    { label: "Words understood", value: `${stats.understood} / ${stats.judged} (${understoodPercent}%)` },
                    { label: "Distinct words", value: stats.distinct }
                ]}
                onBack={goToDashboard}
            />
        );
    }

    if (phase === "generating") {
        return (
            <LoadingScreen
                title="Composing a phrase..."
                subtitle="Alpha AI is preparing your listening prompt"
                onBack={goToDashboard}
                backLabel="Dashboard"
                onSettingsClick={() => setShowSettings(true)}
                settingsMenu={settingsMenu}
            />
        );
    }

    return (
        <div className="sual-root">
            {dashboardButton}
            {settingsButton}
            {settingsMenu}
            <h1>Shut Up &amp; Listen</h1>
            <div className="flashcard-subtitle">
                Listen to the phrase and <strong>type what it means</strong> in English. You will not
                see the words &mdash; go by ear alone.
            </div>

            {noVoice && (
                <div className="sual-voice-warning">
                    Your browser has no voice installed for this language, so the phrase may play
                    silently. Try a different browser, or install the language's speech voice in your
                    system settings.
                </div>
            )}

            {statsBar}

            {(phase === "listening" || phase === "evaluating") && round && (
                <div className="sual-round">
                    <div className="test-listen-area">
                        <SpeakerButton
                            text={round.sentence}
                            lang={langTag}
                            label="Hear the phrase"
                            className="speaker-btn--listen"
                            size={40}
                            rate={speechRate}
                        />
                        <p className="test-listen-hint">Tap to hear it again</p>
                        <button
                            type="button"
                            className="sual-slow-btn"
                            onClick={() => speakText(round.sentence, langTag,
                                { rate: speechRate * SLOW_RATE_FACTOR })}
                        >
                            Play slower
                        </button>
                    </div>

                    <textarea
                        className="test-input sual-answer-input"
                        placeholder="Type what you heard, in English"
                        value={answer}
                        onChange={e => setAnswer(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={phase === "evaluating"}
                        rows={3}
                        autoFocus
                    />

                    <button
                        type="button"
                        className="test-submit-btn"
                        onClick={submitAnswer}
                        disabled={phase === "evaluating"}
                    >
                        {phase === "evaluating" ? "Checking your answer..." : "Submit"}
                    </button>
                </div>
            )}

            {phase === "feedback" && result && round && (
                <div className="sual-feedback">
                    <div className="test-feedback-answer-row test-feedback-answer-row--prompt">
                        <span className="test-feedback-prompt">{round.sentence}</span>
                        <SpeakerButton text={round.sentence} lang={langTag} label="Hear pronunciation" rate={speechRate} />
                    </div>

                    <div className="sual-reveal">
                        <p><strong>Meaning:</strong> {result.reference_translation}</p>
                        <p><strong>You wrote:</strong> {answer.trim() || <em>(nothing)</em>}</p>
                    </div>

                    <div className="sual-word-chips">
                        {result.words.map(word => {
                            let modifier = "sual-word-chip--skipped";
                            if (word.evaluated) {
                                modifier = word.understood ? "sual-word-chip--yes" : "sual-word-chip--no";
                            }
                            return (
                                <button
                                    key={word.word_id}
                                    type="button"
                                    className={`sual-word-chip ${modifier}`}
                                    onClick={() => speakText(word.surface, langTag, { rate: speechRate })}
                                    title={word.evaluated
                                        ? `Familiarity ${word.familiarity_score}`
                                        : "Not graded this round"}
                                >
                                    {word.surface}
                                </button>
                            );
                        })}
                    </div>

                    {result.feedback && <p className="sual-ai-feedback">{result.feedback}</p>}

                    <p className="sual-round-score">
                        {result.understood_count} of {result.evaluated_count} words understood
                    </p>

                    <div className="test-feedback-actions">
                        <button type="button" className="test-submit-btn" onClick={generateRound}>
                            Next phrase
                        </button>
                        <button type="button" className="sual-end-btn" onClick={() => { stopSpeech(); setPhase("summary"); }}>
                            End session
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ListeningGame;
